import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useT } from '@/i18n/I18nProvider';
import { onAccessRestricted, onSessionExpired } from '@/shared/lib/api';
import { queryClient } from '@/shared/lib/queryClient';
import { authApi, demoApi } from './auth.api';
import { useAuthStore, type SignOutReason } from './auth.store';

// Refresh failed → drop the session; ProtectedRoute redirects to /login.
onSessionExpired(() => {
  const { status, signOut } = useAuthStore.getState();
  if (status === 'authenticated') {
    queryClient.clear();
    signOut('expired');
  }
});

// The server says the session is restricted → re-read `/auth/me`, so the gate shows up (the user may have been restricted meanwhile).
onAccessRestricted(() => {
  if (useAuthStore.getState().status !== 'authenticated') return;
  void authApi.me().then((r) => useAuthStore.getState().setUser(r.user));
});

/** Resolves the current session once on app start (`GET /auth/me`). */
export function useAuthBootstrap(): void {
  useEffect(() => {
    let cancelled = false;
    authApi
      .me()
      .then(({ user, features }) => {
        if (cancelled) return;
        useAuthStore.getState().setFeatures(features);
        useAuthStore.getState().setUser(user);
      })
      .catch(() => {
        // No session (or backend unreachable) → anonymous, without a "signed out" notice.
        if (!cancelled) useAuthStore.setState({ user: null, status: 'anonymous', signOutReason: null });
      });
    return () => {
      cancelled = true;
    };
  }, []);
}

/** Ends the session server-side (best effort) and resets client state. */
export function useSignOut() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  return useCallback(
    async (reason: SignOutReason = 'logout') => {
      try {
        await authApi.logout();
      } catch {
        /* session may already be gone — still clear locally */
      }
      qc.clear();
      useAuthStore.getState().signOut(reason);
      navigate('/login', { replace: true });
    },
    [navigate, qc],
  );
}

export const DEMO_USERS_KEY = ['demo', 'users'] as const;

/** Demo users list. 404 → demo mode disabled (switcher hidden). */
export function useDemoUsers() {
  return useQuery({
    queryKey: DEMO_USERS_KEY,
    queryFn: demoApi.users,
    staleTime: Infinity,
    retry: false,
    meta: { silent: true },
    select: (users) => users ?? [],
  });
}

/** Switch the session to another demo user and refetch everything under the new identity. */
export function useDemoSwitch() {
  const qc = useQueryClient();
  const t = useT();
  return useMutation({
    mutationFn: demoApi.switchTo,
    onSuccess: async (user) => {
      useAuthStore.getState().setUser(user);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'branding' && q.queryKey[0] !== 'demo' });
      await qc.refetchQueries({ type: 'active' });
      toast.success(t('demo.switched', { name: `${user.firstName} ${user.lastName}` }));
    },
  });
}
